import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  BarChart3,
  Edit2,
  Image,
  Mail,
  MessageSquare,
  Package,
  Plus,
  RefreshCw,
  Save,
  Settings,
  ShoppingBag,
  Trash2,
  X,
} from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "@/lib/auth";
import {
  createArtwork,
  deleteArtwork,
  getAdminStats,
  getArtworks,
  getContactMessages,
  getOrders,
  getStoreSettings,
  markContactMessageRead,
  saveStoreSettings,
  updateArtwork,
  updateOrderStatus,
  uploadImage,
  type AdminStats,
  type Artwork,
  type ContactMessage,
  type Order,
  type PrintShippingConfig,
  type StoreSettings,
} from "@/lib/api";
import { formatPrice, getArtworkPreviewImage } from "@/lib/utils";
import { getPrintSizePrices, type PrintSizePrice } from "@/lib/printSizes";
import { getCountries } from "@/lib/countries";

type Tab = "dashboard" | "artworks" | "add" | "orders" | "messages" | "settings";

const emptySettings: StoreSettings = {
  contact_email: "",
  contact_phone: "",
  studio_address: "",
  tiktok_url: "",
  checkout_note: "",
  home_hero_image: "",
  shop_all_cover_image: "",
  print_category_image: "",
  original_category_image: "",
  print_shipping_config: { internationalBaseAmount: 0, countryOverrides: [] },
};

const orderStatusLabels: Record<Order["status"], string> = {
  pending: "In attesa", paid: "Pagato", processing: "In preparazione", shipped: "Spedito",
  delivered: "Consegnato", cancelled: "Annullato", refunded: "Rimborsato",
};

export default function Admin() {
  const { user, initialized, checkAuth } = useAuth();
  const navigate = useNavigate();
  const [tab, setTab] = useState<Tab>("dashboard");
  const [stats, setStats] = useState<AdminStats | null>(null);
  const [artworks, setArtworks] = useState<Artwork[]>([]);
  const [orders, setOrders] = useState<Order[]>([]);
  const [messages, setMessages] = useState<ContactMessage[]>([]);
  const [settings, setSettings] = useState<StoreSettings>(emptySettings);
  const [editing, setEditing] = useState<Artwork | null>(null);
  const [search, setSearch] = useState("");
  const [loading, setLoading] = useState(true);

  const tabs: { key: Tab; label: string; icon: React.ReactNode }[] = [
    { key: "dashboard", label: "Dashboard", icon: <BarChart3 size={17} /> },
    { key: "artworks", label: "Opere", icon: <Image size={17} /> },
    { key: "add", label: "Nuova", icon: <Plus size={17} /> },
    { key: "orders", label: "Ordini", icon: <Package size={17} /> },
    { key: "messages", label: "Messaggi", icon: <MessageSquare size={17} /> },
    { key: "settings", label: "Negozio", icon: <Settings size={17} /> },
  ];

  const filteredArtworks = useMemo(() => {
    const term = search.trim().toLowerCase();
    if (!term) return artworks;
    return artworks.filter((artwork) =>
      [artwork.title_en, artwork.title_it, artwork.category, artwork.status].some((value) =>
        String(value || "").toLowerCase().includes(term)
      )
    );
  }, [artworks, search]);

  useEffect(() => {
    if (!initialized) {
      checkAuth();
      return;
    }
    if (!user || user.role !== "admin") {
      navigate("/login");
      return;
    }
    void loadData();
  }, [initialized, user, navigate, checkAuth]);

  async function loadData() {
    setLoading(true);
    try {
      const [statsData, artworksData, ordersData, messagesData, settingsData] = await Promise.all([
        getAdminStats(),
        getArtworks({ status: "all", limit: 200 }),
        getOrders(),
        getContactMessages(),
        getStoreSettings(),
      ]);
      setStats(statsData);
      setArtworks(artworksData.artworks);
      setOrders(ordersData.orders);
      setMessages(messagesData.messages);
      setSettings(settingsData);
    } catch (error) {
      toast.error(error instanceof Error ? error.message : "Impossibile caricare l'area admin");
    } finally {
      setLoading(false);
    }
  }

  if (!initialized || loading) {
    return (
      <div className="pt-24 pb-16">
        <div className="container">
          <p style={{ color: "var(--color-text-muted)" }}>Caricamento area admin...</p>
        </div>
      </div>
    );
  }

  if (!user || user.role !== "admin") return null;

  return (
    <div className="pt-24 pb-16">
      <div className="container">
        <div className="mb-8 flex flex-col gap-4 sm:flex-row sm:items-end sm:justify-between">
          <div>
            <p className="mb-2 text-xs uppercase tracking-[0.35em]" style={{ color: "var(--color-text-light)" }}>
              Dalila studio
            </p>
            <h1 className="text-3xl" style={{ fontFamily: "var(--font-display)", color: "var(--color-charcoal)" }}>
              Admin
            </h1>
          </div>
          <button onClick={() => void loadData()} className="btn-secondary flex w-fit items-center gap-2">
            <RefreshCw size={16} />
            Aggiorna
          </button>
        </div>

        <div className="mb-8 flex flex-wrap gap-2 border-b" style={{ borderColor: "var(--color-border)" }}>
          {tabs.map((item) => (
            <button
              key={item.key}
              onClick={() => {
                setTab(item.key);
                setEditing(null);
              }}
              className={`flex items-center gap-2 border-b-2 px-3 py-3 text-sm transition-colors ${
                tab === item.key
                  ? "border-[var(--color-terracotta)] text-[var(--color-terracotta)]"
                  : "border-transparent text-[var(--color-text-muted)] hover:text-[var(--color-charcoal)]"
              }`}
            >
              {item.icon}
              {item.label}
            </button>
          ))}
        </div>

        {tab === "dashboard" && stats && <Dashboard stats={stats} />}

        {tab === "artworks" && (
          <section className="space-y-5">
            <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
              <input
                value={search}
                onChange={(event) => setSearch(event.target.value)}
                placeholder="Cerca per titolo, categoria o stato"
                className="w-full border bg-white px-4 py-3 text-sm focus:outline-none sm:max-w-sm"
                style={{ borderColor: "var(--color-border)" }}
              />
              <button onClick={() => setTab("add")} className="btn-primary flex w-fit items-center gap-2">
                <Plus size={16} />
                Aggiungi opera
              </button>
            </div>

            {editing && (
              <AdminPanel title={`Modifica: ${editing.title_it}`} onClose={() => setEditing(null)}>
                <ArtworkForm
                  key={editing.id}
                  artwork={editing}
                  onSuccess={async () => {
                    setEditing(null);
                    await loadData();
                  }}
                />
              </AdminPanel>
            )}

            <div className="grid gap-4">
              {filteredArtworks.map((artwork) => (
                <div key={artwork.id} className="grid gap-4 border bg-white p-4 sm:grid-cols-[84px_1fr_auto]" style={{ borderColor: "var(--color-border)" }}>
                  <img src={getArtworkPreviewImage(artwork)} alt={artwork.title_en} className="h-24 w-24 object-cover sm:h-20 sm:w-20" />
                  <div className="min-w-0">
                    <div className="mb-1 flex flex-wrap items-center gap-2">
                      <p className="font-medium" style={{ color: "var(--color-charcoal)" }}>{artwork.title_en}</p>
                      {artwork.featured ? <Badge>in evidenza</Badge> : null}
                    </div>
                    <p className="text-xs uppercase tracking-[0.22em]" style={{ color: "var(--color-text-light)" }}>
                      {artwork.category} · {artwork.status} · disponibilità {artwork.stock_quantity ?? 1}
                    </p>
                    <p className="mt-2 text-sm" style={{ color: "var(--color-text-muted)" }}>
                      {formatPrice(artwork.price, artwork.currency)}
                    </p>
                  </div>
                  <div className="flex items-start gap-2">
                    <button onClick={() => setEditing(artwork)} className="p-2 hover:text-[var(--color-terracotta)]" style={{ color: "var(--color-text-muted)" }} aria-label="Modifica opera">
                      <Edit2 size={17} />
                    </button>
                    <button
                      onClick={async () => {
                        if (!confirm("Eliminare questa opera?")) return;
                        try {
                          await deleteArtwork(artwork.id);
                          toast.success("Opera eliminata");
                          await loadData();
                        } catch (error) {
                          toast.error(error instanceof Error ? error.message : "Impossibile eliminare l'opera");
                        }
                      }}
                      className="p-2 hover:text-red-500"
                      style={{ color: "var(--color-text-muted)" }}
                      aria-label="Elimina opera"
                    >
                      <Trash2 size={17} />
                    </button>
                  </div>
                </div>
              ))}
              {filteredArtworks.length === 0 && <EmptyState text="Nessuna opera corrisponde al filtro." />}
            </div>
          </section>
        )}

        {tab === "add" && (
          <AdminPanel title="Nuova opera">
            <ArtworkForm onSuccess={async () => {
              toast.success("Opera creata");
              setTab("artworks");
              await loadData();
            }} />
          </AdminPanel>
        )}

        {tab === "orders" && <OrdersPanel orders={orders} onChange={loadData} />}
        {tab === "messages" && <MessagesPanel messages={messages} onChange={loadData} />}
        {tab === "settings" && <SettingsPanel settings={settings} setSettings={setSettings} onSaved={loadData} />}
      </div>
    </div>
  );
}

function Dashboard({ stats }: { stats: AdminStats }) {
  return (
    <div className="space-y-8">
      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4">
        <StatCard label="Opere" value={stats.totalArtworks} icon={<ShoppingBag size={18} />} />
        <StatCard label="Disponibili" value={stats.availableArtworks} icon={<Image size={18} />} />
        <StatCard label="Ordini attivi" value={stats.pendingOrders ?? 0} icon={<Package size={18} />} />
        <StatCard label="Messaggi non letti" value={stats.unreadMessages} icon={<Mail size={18} />} />
        <StatCard label="Vendute" value={stats.soldArtworks} />
        <StatCard label="Riservate" value={stats.reservedArtworks ?? 0} />
        <StatCard label="Ordini pagati" value={stats.totalOrders} />
        <StatCard label="Revenue" value={formatPrice(stats.totalRevenue)} />
      </div>

      <AdminPanel title="Ultimi ordini">
        <div className="space-y-3">
          {(stats.recentOrders || []).map((order) => (
            <OrderRow key={order.id} order={order} compact />
          ))}
          {(!stats.recentOrders || stats.recentOrders.length === 0) && <EmptyState text="Non ci sono ancora ordini." />}
        </div>
      </AdminPanel>
    </div>
  );
}

function ArtworkForm({ artwork, onSuccess }: { artwork?: Artwork; onSuccess: () => void | Promise<void> }) {
  const [saving, setSaving] = useState(false);
  const [imageFile, setImageFile] = useState<File | null>(null);
  const [additionalImageFiles, setAdditionalImageFiles] = useState<File[]>([]);
  const [form, setForm] = useState({
    title_it: artwork?.title_it || "",
    title_en: artwork?.title_en || "",
    description_it: artwork?.description_it || "",
    description_en: artwork?.description_en || "",
    category: artwork?.category || "original",
    technique: artwork?.technique || "",
    dimensions: artwork?.dimensions || "",
    year: artwork?.year || new Date().getFullYear(),
    price: artwork?.price || 0,
    currency: artwork?.currency || "EUR",
    status: artwork?.status || "available",
    stock_quantity: artwork?.stock_quantity ?? (artwork?.category === "print" ? 10 : 1),
    featured: Boolean(artwork?.featured),
    image_key: artwork?.image_key || null,
    image_url: artwork?.image_url || null,
    additional_images: artwork?.additional_images || null,
  });
  const [additionalImages, setAdditionalImages] = useState<string[]>(() => parseAdditionalImages(artwork?.additional_images));
  const previewImage = form.image_url || additionalImages[0] || null;
  const [printSizes, setPrintSizes] = useState<PrintSizePrice[]>(() => artwork ? getPrintSizePrices(artwork) : [
    { size: "20 × 15 cm", price: 0 },
    { size: "40 × 30 cm", price: 0 },
  ]);

  async function handleSubmit(event: React.FormEvent) {
    event.preventDefault();
    setSaving(true);
    try {
      let nextForm = form;
      if (imageFile) {
        const upload = await uploadImage(imageFile, "artworks");
        nextForm = { ...nextForm, image_key: upload.key, image_url: upload.url };
      }
      const additionalUploads = additionalImageFiles.length > 0
        ? await Promise.all(additionalImageFiles.map((file) => uploadImage(file, "artworks")))
        : [];
      const remainingImages = [...additionalImages, ...additionalUploads.map((upload) => upload.url)];
      const mainImage = nextForm.image_url || remainingImages.shift() || null;
      nextForm = { ...nextForm, image_url: mainImage, additional_images: remainingImages.length > 0 ? JSON.stringify(remainingImages) : null };

      const payload = {
        ...nextForm,
        year: Number(nextForm.year) || null,
        price: nextForm.category === "print"
          ? Math.min(...printSizes.map((item) => Number(item.price) || 0))
          : Number(nextForm.price) || 0,
        print_sizes: nextForm.category === "print" ? JSON.stringify(printSizes) : null,
        stock_quantity: Number(nextForm.stock_quantity) || 0,
        featured: nextForm.featured ? 1 : 0,
      } as Partial<Artwork>;

      if (artwork) {
        await updateArtwork(artwork.id, payload);
        toast.success("Opera aggiornata");
      } else {
        await createArtwork(payload);
        toast.success("Opera creata");
      }
      await onSuccess();
    } catch (error) {
      toast.error(error instanceof Error ? error.message : "Impossibile salvare l'opera");
    } finally {
      setSaving(false);
    }
  }

  return (
    <form onSubmit={handleSubmit} className="grid gap-5">
      <div className="grid gap-4 sm:grid-cols-2">
        <Field label="Titolo IT *"><input required value={form.title_it} onChange={(e) => setForm({ ...form, title_it: e.target.value })} className="admin-input" /></Field>
        <Field label="Titolo EN *"><input required value={form.title_en} onChange={(e) => setForm({ ...form, title_en: e.target.value })} className="admin-input" /></Field>
      </div>
      <div className="grid gap-4 sm:grid-cols-2">
        <Field label="Descrizione IT"><textarea rows={4} value={form.description_it} onChange={(e) => setForm({ ...form, description_it: e.target.value })} className="admin-input resize-none" /></Field>
        <Field label="Descrizione EN"><textarea rows={4} value={form.description_en} onChange={(e) => setForm({ ...form, description_en: e.target.value })} className="admin-input resize-none" /></Field>
      </div>
      <div className="grid gap-4 sm:grid-cols-4">
        <Field label="Categoria"><select value={form.category} onChange={(e) => setForm({ ...form, category: e.target.value as Artwork["category"] })} className="admin-input"><option value="original">Original</option><option value="print">Print</option></select></Field>
        <Field label="Stato"><select value={form.status} onChange={(e) => setForm({ ...form, status: e.target.value as Artwork["status"] })} className="admin-input"><option value="available">Disponibile</option><option value="reserved">Riservato</option><option value="sold">Venduto</option></select></Field>
        <Field label="Disponibilità"><input type="number" min={0} value={form.stock_quantity} onChange={(e) => setForm({ ...form, stock_quantity: Number(e.target.value) })} className="admin-input" /></Field>
        <Field label="Anno"><input type="number" value={form.year} onChange={(e) => setForm({ ...form, year: Number(e.target.value) })} className="admin-input" /></Field>
      </div>
      <div className="grid gap-4 sm:grid-cols-4">
        {form.category === "print" ? <div className="space-y-3 sm:col-span-2">
          <span className="text-sm" style={{ color: "var(--color-text-muted)" }}>Dimensioni e prezzi *</span>
          {printSizes.map((item, index) => (
            <div key={index} className="grid gap-2 sm:grid-cols-[1fr_150px_auto]">
              <input required value={item.size} onChange={(e) => setPrintSizes(printSizes.map((current, itemIndex) => itemIndex === index ? { ...current, size: e.target.value } : current))} placeholder="Es. 50 × 70 cm" className="admin-input" aria-label={`Dimensione ${index + 1}`} />
              <input type="number" min={0.01} step="0.01" required value={item.price || ""} onChange={(e) => setPrintSizes(printSizes.map((current, itemIndex) => itemIndex === index ? { ...current, price: Number(e.target.value) } : current))} placeholder="Prezzo" className="admin-input" aria-label={`Prezzo ${index + 1}`} />
              <button type="button" disabled={printSizes.length === 1} onClick={() => setPrintSizes(printSizes.filter((_, itemIndex) => itemIndex !== index))} className="btn-ghost px-2 text-sm disabled:cursor-not-allowed disabled:opacity-40">Rimuovi</button>
            </div>
          ))}
          <button type="button" onClick={() => setPrintSizes([...printSizes, { size: "", price: 0 }])} className="btn-secondary flex items-center gap-2 text-sm"><Plus size={15} /> Aggiungi dimensione</button>
        </div> : <Field label="Prezzo"><input type="number" min={0.01} step="0.01" required value={form.price} onChange={(e) => setForm({ ...form, price: Number(e.target.value) })} className="admin-input" /></Field>}
        <Field label="Valuta"><input value={form.currency} onChange={(e) => setForm({ ...form, currency: e.target.value.toUpperCase() })} className="admin-input" /></Field>
        <Field label="Tecnica"><input value={form.technique} onChange={(e) => setForm({ ...form, technique: e.target.value })} className="admin-input" /></Field>
        <Field label="Dimensioni"><input value={form.dimensions} onChange={(e) => setForm({ ...form, dimensions: e.target.value })} className="admin-input" /></Field>
      </div>
      <div className="grid gap-4 sm:grid-cols-[160px_1fr]">
        <div>{previewImage ? <img src={previewImage} alt="" className="h-40 w-40 object-cover" /> : <div className="flex h-40 w-40 items-center justify-center border text-sm" style={{ borderColor: "var(--color-border)", color: "var(--color-text-light)" }}>Nessuna immagine</div>}</div>
        <div className="space-y-4">
          <Field label="Immagine principale *"><input type="file" required={!artwork && !form.image_url && additionalImageFiles.length === 0} accept="image/jpeg,image/png,image/webp" onChange={(e) => setImageFile(e.target.files?.[0] || null)} className="w-full text-sm" /></Field>
          <Field label="Foto aggiuntive"><input type="file" accept="image/jpeg,image/png,image/webp" multiple onChange={(e) => setAdditionalImageFiles(Array.from(e.target.files || []))} className="w-full text-sm" /><p className="mt-1 text-xs" style={{ color: "var(--color-text-light)" }}>Puoi selezionare più immagini. Se non scegli una principale, la prima verrà usata come copertina.</p></Field>
          {additionalImageFiles.length > 0 ? <p className="text-sm" style={{ color: "var(--color-text-muted)" }}>{additionalImageFiles.length} {additionalImageFiles.length === 1 ? "foto da caricare" : "foto da caricare"}</p> : null}
          <label className="flex items-center gap-2 text-sm" style={{ color: "var(--color-text-muted)" }}>
            <input type="checkbox" checked={form.featured} onChange={(e) => setForm({ ...form, featured: e.target.checked })} />
            In evidenza sul sito
          </label>
        </div>
      </div>
      {additionalImages.length > 0 && <div>
        <p className="mb-2 text-sm" style={{ color: "var(--color-text-muted)" }}>Foto aggiuntive</p>
        <div className="flex flex-wrap gap-3">
          {additionalImages.map((image) => <div key={image} className="relative h-24 w-24 overflow-hidden border" style={{ borderColor: "var(--color-border)" }}>
            <img src={image} alt="" className="h-full w-full object-cover" />
            <button type="button" onClick={() => setAdditionalImages(additionalImages.filter((item) => item !== image))} className="absolute right-1 top-1 rounded-full bg-white/90 p-1 text-xs" aria-label="Rimuovi foto"><X size={14} /></button>
          </div>)}
        </div>
      </div>}
      <button type="submit" disabled={saving} className="btn-primary flex w-fit items-center gap-2 disabled:opacity-50">
        <Save size={16} />
        {saving ? "Salvataggio..." : artwork ? "Salva modifiche" : "Crea opera"}
      </button>
    </form>
  );
}

function parseAdditionalImages(value: string | null | undefined): string[] {
  if (!value) return [];
  try {
    const parsed = JSON.parse(value) as unknown;
    return Array.isArray(parsed) ? parsed.filter((item): item is string => typeof item === "string" && item.length > 0) : [];
  } catch {
    return [];
  }
}

function OrdersPanel({ orders, onChange }: { orders: Order[]; onChange: () => void | Promise<void> }) {
  return (
    <AdminPanel title="Ordini">
      <div className="space-y-3">
        {orders.map((order) => (
          <OrderRow key={order.id} order={order} onChange={onChange} />
        ))}
        {orders.length === 0 && <EmptyState text="Non ci sono ancora ordini." />}
      </div>
    </AdminPanel>
  );
}

function OrderRow({ order, onChange, compact }: { order: Order; onChange?: () => void | Promise<void>; compact?: boolean }) {
  const [saving, setSaving] = useState(false);
  const [status, setStatus] = useState<Order["status"]>(order.status);
  const [trackingUrl, setTrackingUrl] = useState(order.tracking_url || "");

  async function handleStatus() {
    if (!onChange) return;
    setSaving(true);
    try {
      const result = await updateOrderStatus(order.id, status, trackingUrl);
      toast.success(result.notified ? "Stato aggiornato e email inviata all'acquirente" : "Aggiornamento salvato");
      await onChange();
    } catch (error) {
      toast.error(error instanceof Error ? error.message : "Impossibile aggiornare l'ordine");
    } finally {
      setSaving(false);
    }
  }

  return (
    <div className="grid gap-3 border p-4 sm:grid-cols-[1fr_auto]" style={{ borderColor: "var(--color-border)" }}>
      <div>
        <p className="font-medium" style={{ color: "var(--color-charcoal)" }}>Ordine #{order.id} · {formatPrice(order.total_amount, order.currency)}</p>
        <p className="mt-1 text-sm" style={{ color: "var(--color-text-muted)" }}>{order.item_titles || "Senza dettagli"} · {order.customer_email || "senza email"}</p>
        {!compact && order.shipping_address ? <p className="mt-2 text-xs" style={{ color: "var(--color-text-light)" }}>{order.shipping_address}</p> : null}
      </div>
      {compact ? <Badge>{orderStatusLabels[order.status]}</Badge> : (
        <div className="grid gap-2 sm:min-w-72">
          <select disabled={saving} value={status} onChange={(event) => setStatus(event.target.value as Order["status"])} className="admin-input">
            {(Object.keys(orderStatusLabels) as Order["status"][]).map((value) => <option key={value} value={value}>{orderStatusLabels[value]}</option>)}
          </select>
          <input type="url" value={trackingUrl} onChange={(event) => setTrackingUrl(event.target.value)} placeholder="Link di tracciamento (https://...)" className="admin-input" />
          <button disabled={saving} onClick={() => void handleStatus()} className="btn-secondary text-sm disabled:opacity-50">{saving ? "Salvataggio..." : "Salva aggiornamento"}</button>
        </div>
      )}
    </div>
  );
}

function MessagesPanel({ messages, onChange }: { messages: ContactMessage[]; onChange: () => void | Promise<void> }) {
  return (
    <AdminPanel title="Messaggi">
      <div className="space-y-3">
        {messages.map((message) => (
          <div key={message.id} className="border p-4" style={{ borderColor: message.read ? "var(--color-border)" : "var(--color-terracotta)" }}>
            <div className="mb-2 flex flex-col gap-2 sm:flex-row sm:items-start sm:justify-between">
              <div>
                <p className="font-medium" style={{ color: "var(--color-charcoal)" }}>{message.subject || "Senza oggetto"}</p>
                <p className="text-sm" style={{ color: "var(--color-text-muted)" }}>{message.name} · {message.email}</p>
              </div>
              {!message.read && <button onClick={async () => { await markContactMessageRead(message.id); await onChange(); }} className="btn-secondary text-xs">Segna come letto</button>}
            </div>
            <p className="whitespace-pre-wrap text-sm leading-relaxed" style={{ color: "var(--color-text-muted)" }}>{message.message}</p>
          </div>
        ))}
        {messages.length === 0 && <EmptyState text="Non ci sono messaggi." />}
      </div>
    </AdminPanel>
  );
}

function SettingsPanel({ settings, setSettings, onSaved }: { settings: StoreSettings; setSettings: (settings: StoreSettings) => void; onSaved: () => void | Promise<void> }) {
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState<keyof StoreSettings | null>(null);
  const countries = useMemo(() => getCountries("it").filter((country) => country.code !== "IT"), []);

  function updatePrintShipping(config: PrintShippingConfig) {
    setSettings({ ...settings, print_shipping_config: config });
  }

  async function uploadStorefrontImage(key: keyof StoreSettings, file: File | null) {
    if (!file) return;
    setUploading(key);
    try {
      const upload = await uploadImage(file, "storefront");
      setSettings({ ...settings, [key]: upload.url });
      toast.success("Immagine caricata. Salva le impostazioni per pubblicarla.");
    } catch (error) {
      toast.error(error instanceof Error ? error.message : "Impossibile caricare l'immagine");
    } finally {
      setUploading(null);
    }
  }

  async function handleSubmit(event: React.FormEvent) {
    event.preventDefault();
    setSaving(true);
    try {
      const saved = await saveStoreSettings(settings);
      setSettings(saved);
      toast.success("Configuración guardada");
      await onSaved();
    } catch (error) {
      toast.error(error instanceof Error ? error.message : "Impossibile salvare le impostazioni");
    } finally {
      setSaving(false);
    }
  }

  return (
    <AdminPanel title="Impostazioni del negozio">
      <form onSubmit={handleSubmit} className="grid gap-4">
        <div className="grid gap-4 sm:grid-cols-2">
          <Field label="Email di contatto"><input value={settings.contact_email} onChange={(e) => setSettings({ ...settings, contact_email: e.target.value })} className="admin-input" /></Field>
          <Field label="Telefono"><input value={settings.contact_phone} onChange={(e) => setSettings({ ...settings, contact_phone: e.target.value })} className="admin-input" /></Field>
        </div>
        <Field label="Indirizzo / studio"><textarea rows={3} value={settings.studio_address} onChange={(e) => setSettings({ ...settings, studio_address: e.target.value })} className="admin-input resize-none" /></Field>
        <Field label="URL TikTok"><input type="url" value={settings.tiktok_url} onChange={(e) => setSettings({ ...settings, tiktok_url: e.target.value })} placeholder="https://www.tiktok.com/@..." className="admin-input" /></Field>
        <Field label="Avviso checkout"><textarea rows={3} value={settings.checkout_note} onChange={(e) => setSettings({ ...settings, checkout_note: e.target.value })} className="admin-input resize-none" /></Field>
        <section className="border-t pt-5" style={{ borderColor: "var(--color-border)" }}>
          <div className="mb-4">
            <p className="text-sm font-medium" style={{ color: "var(--color-charcoal)" }}>Spedizione delle print</p>
            <p className="mt-1 text-xs" style={{ color: "var(--color-text-muted)" }}>In Italia la spedizione è inclusa nel prezzo. Per gli altri paesi viene addebitato un solo supplemento per ordine.</p>
          </div>
          <div className="grid gap-4">
            <Field label="Tariffa internazionale base (EUR)"><input type="number" min={0} max={100000} step="0.01" required value={settings.print_shipping_config.internationalBaseAmount} onChange={(event) => updatePrintShipping({ ...settings.print_shipping_config, internationalBaseAmount: Number(event.target.value) })} className="admin-input" /></Field>
            <div>
              <div className="mb-2 flex items-center justify-between gap-3">
                <span className="text-sm" style={{ color: "var(--color-text-muted)" }}>Eccezioni per paese</span>
                <button type="button" onClick={() => {
                  const available = countries.find((country) => !settings.print_shipping_config.countryOverrides.some((override) => override.countryCode === country.code));
                  if (!available) return;
                  updatePrintShipping({ ...settings.print_shipping_config, countryOverrides: [...settings.print_shipping_config.countryOverrides, { countryCode: available.code, amount: settings.print_shipping_config.internationalBaseAmount }] });
                }} className="btn-secondary text-xs">Aggiungi eccezione</button>
              </div>
              <div className="grid gap-2">
                {settings.print_shipping_config.countryOverrides.map((override, index) => (
                  <div key={`${override.countryCode}-${index}`} className="grid gap-2 sm:grid-cols-[1fr_10rem_auto]">
                    <select value={override.countryCode} onChange={(event) => updatePrintShipping({ ...settings.print_shipping_config, countryOverrides: settings.print_shipping_config.countryOverrides.map((item, itemIndex) => itemIndex === index ? { ...item, countryCode: event.target.value } : item) })} className="admin-input">
                      {countries.map((country) => <option key={country.code} value={country.code} disabled={settings.print_shipping_config.countryOverrides.some((item, itemIndex) => itemIndex !== index && item.countryCode === country.code)}>{country.name}</option>)}
                    </select>
                    <input type="number" min={0} max={100000} step="0.01" required value={override.amount} onChange={(event) => updatePrintShipping({ ...settings.print_shipping_config, countryOverrides: settings.print_shipping_config.countryOverrides.map((item, itemIndex) => itemIndex === index ? { ...item, amount: Number(event.target.value) } : item) })} className="admin-input" aria-label={`Tariffa per ${override.countryCode}`} />
                    <button type="button" onClick={() => updatePrintShipping({ ...settings.print_shipping_config, countryOverrides: settings.print_shipping_config.countryOverrides.filter((_, itemIndex) => itemIndex !== index) })} className="btn-ghost px-2 text-sm">Rimuovi</button>
                  </div>
                ))}
                {settings.print_shipping_config.countryOverrides.length === 0 && <p className="text-xs" style={{ color: "var(--color-text-light)" }}>Nessuna eccezione: verrà applicata la tariffa internazionale base.</p>}
              </div>
            </div>
          </div>
        </section>
        <div className="border-t pt-5" style={{ borderColor: "var(--color-border)" }}>
          <p className="mb-4 text-sm font-medium" style={{ color: "var(--color-charcoal)" }}>Immagini del sito</p>
          <div className="grid gap-5 sm:grid-cols-2">
            <StorefrontImageField label="Copertina Home" value={settings.home_hero_image} uploading={uploading === "home_hero_image"} onChange={(value) => setSettings({ ...settings, home_hero_image: value })} onUpload={(file) => void uploadStorefrontImage("home_hero_image", file)} />
            <StorefrontImageField label="Copertina Shop all" value={settings.shop_all_cover_image} uploading={uploading === "shop_all_cover_image"} onChange={(value) => setSettings({ ...settings, shop_all_cover_image: value })} onUpload={(file) => void uploadStorefrontImage("shop_all_cover_image", file)} />
            <StorefrontImageField label="Copertina Print" value={settings.print_category_image} uploading={uploading === "print_category_image"} onChange={(value) => setSettings({ ...settings, print_category_image: value })} onUpload={(file) => void uploadStorefrontImage("print_category_image", file)} />
            <StorefrontImageField label="Copertina Original" value={settings.original_category_image} uploading={uploading === "original_category_image"} onChange={(value) => setSettings({ ...settings, original_category_image: value })} onUpload={(file) => void uploadStorefrontImage("original_category_image", file)} />
          </div>
        </div>
        <button type="submit" disabled={saving} className="btn-primary flex w-fit items-center gap-2 disabled:opacity-50">
          <Save size={16} />
        {saving ? "Salvataggio..." : "Salva impostazioni"}
        </button>
      </form>
    </AdminPanel>
  );
}

function StorefrontImageField({ label, value, uploading, onChange, onUpload }: { label: string; value: string; uploading: boolean; onChange: (value: string) => void; onUpload: (file: File | null) => void }) {
  return (
    <div className="grid gap-2">
      <span className="text-sm" style={{ color: "var(--color-text-muted)" }}>{label}</span>
      {value ? <img src={value} alt="" className="h-32 w-full border object-cover" style={{ borderColor: "var(--color-border)" }} /> : <div className="flex h-32 items-center justify-center border text-sm" style={{ borderColor: "var(--color-border)", color: "var(--color-text-light)" }}>Nessuna immagine</div>}
      <input value={value} onChange={(event) => onChange(event.target.value)} placeholder="URL immagine" className="admin-input" />
      <input type="file" accept="image/jpeg,image/png,image/webp" disabled={uploading} onChange={(event) => onUpload(event.target.files?.[0] || null)} className="w-full text-sm" />
      {uploading ? <span className="text-xs" style={{ color: "var(--color-text-light)" }}>Caricamento...</span> : null}
    </div>
  );
}

function AdminPanel({ title, children, onClose }: { title: string; children: React.ReactNode; onClose?: () => void }) {
  return (
    <section className="border bg-white p-5 sm:p-6" style={{ borderColor: "var(--color-border)" }}>
      <div className="mb-5 flex items-center justify-between gap-4">
        <h2 className="text-xl" style={{ fontFamily: "var(--font-display)", color: "var(--color-charcoal)" }}>{title}</h2>
        {onClose ? <button onClick={onClose} className="p-1" aria-label="Chiudi"><X size={18} /></button> : null}
      </div>
      {children}
    </section>
  );
}

function StatCard({ label, value, icon }: { label: string; value: string | number; icon?: React.ReactNode }) {
  return (
    <div className="border bg-white p-5" style={{ borderColor: "var(--color-border)" }}>
      <div className="mb-4 flex items-center justify-between" style={{ color: "var(--color-text-light)" }}>
        <p className="text-xs uppercase tracking-[0.25em]">{label}</p>
        {icon}
      </div>
      <p className="text-2xl" style={{ fontFamily: "var(--font-display)", color: "var(--color-charcoal)" }}>{value}</p>
    </div>
  );
}

function Field({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <label className="block">
      <span className="mb-1.5 block text-sm" style={{ color: "var(--color-text-muted)" }}>{label}</span>
      {children}
    </label>
  );
}

function Badge({ children }: { children: React.ReactNode }) {
  return <span className="border px-2 py-0.5 text-[10px] uppercase tracking-[0.18em]" style={{ borderColor: "var(--color-border)", color: "var(--color-text-light)" }}>{children}</span>;
}

function EmptyState({ text }: { text: string }) {
  return <p className="border px-4 py-8 text-center text-sm" style={{ borderColor: "var(--color-border)", color: "var(--color-text-muted)" }}>{text}</p>;
}
